import { useState, useEffect, useContext, createContext } from 'react';
import type { ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { User } from '@openclassrooms/sdk-web';

import useGet from '../../../hooks/useGet';

import {
  getCachedCurrentUser,
  setCachedCurrentUser,
  clearCachedCurrentUser,
  UserWithExpiryTime,
} from '../services/cachedCurrentUser';

type CommonContextValues = {
  refetch: () => void;
  clear: () => Promise<UserWithExpiryTime>;
  expiryTime: number;
  isPending: boolean;
};

type ContextWithConnectedUser = {
  isConnected: true;
  currentUser: User;
  userId: number;
};

type ContextWithoutConnectedUser = {
  isConnected: false;
  currentUser: null;
  userId: null;
};

export type CurrentUserContextValue = CommonContextValues &
  (ContextWithConnectedUser | ContextWithoutConnectedUser);

function isConnected(user: User | null): user is User {
  return Boolean(user?.id);
}

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

type CurrentUserContextProviderProps = {
  children: ReactNode;
};

export const CurrentUserContextProvider = ({ children }: CurrentUserContextProviderProps) => {
  const [canFetchAPI, setCanFetchAPI] = useState(false);
  const [currentUserWithExpiryTimeFromCache, setCurrentUserWithExpiryTimeFromCache] =
    useState<UserWithExpiryTime | null>(null);
  const [isLoadingFromStorage, setIsLoadingFromStorage] = useState(true);

  const queryClient = useQueryClient();

  useEffect(() => {
    getCachedCurrentUser().then((currentUserWithExpiryTime: UserWithExpiryTime | null) => {
      if (currentUserWithExpiryTime === null) {
        setCanFetchAPI(true);
      } else {
        setCurrentUserWithExpiryTimeFromCache(currentUserWithExpiryTime);
      }

      setIsLoadingFromStorage(false);
    });
  }, []);

  const cacheUser = async (userToSave: User | null) => {
    const newCachedUserWithExpiryTime = await setCachedCurrentUser(userToSave);
    setCurrentUserWithExpiryTimeFromCache(newCachedUserWithExpiryTime);
  };

  const {
    refetch,
    isPending: isLoadingFromAPI,
    isError,
    data: currentUserData,
  } = useGet('/me', {
    enabled: canFetchAPI,
    queryKey: 'currentUser',
    onSuccess(response) {
      // istanbul ignore next
      cacheUser(response?.body ?? null);
    },
    onError: () => cacheUser(null),
  });

  const currentUserFromCache = currentUserWithExpiryTimeFromCache?.user ?? null;
  const currentUserFromAPI = currentUserData?.body ?? null;
  const expiryTime = currentUserWithExpiryTimeFromCache?.expiryTime;

  const currentUser = currentUserFromAPI ?? currentUserFromCache;

  const isPending =
    !currentUser &&
    (isLoadingFromStorage ||
      // istanbul ignore next
      (isLoadingFromAPI && canFetchAPI) ||
      (!isError && !expiryTime));

  const commonContextValues: CommonContextValues = {
    refetch,
    clear: /* istanbul ignore next */ () => {
      setCurrentUserWithExpiryTimeFromCache(null);
      queryClient.resetQueries({ queryKey: ['currentUser'] });
      return clearCachedCurrentUser();
    },
    expiryTime: expiryTime ?? Infinity,
    isPending,
  };

  const contextValue: CurrentUserContextValue = {
    ...commonContextValues,
    ...(isConnected(currentUser)
      ? {
          currentUser,
          userId: currentUser.id,
          isConnected: true,
        }
      : { currentUser: null, isConnected: false, userId: null }),
  };

  return <CurrentUserContext.Provider value={contextValue}>{children}</CurrentUserContext.Provider>;
};

export const useCurrentUserContext = (): CurrentUserContextValue => {
  const context = useContext(CurrentUserContext);

  if (!context) {
    throw new Error('useCurrentUserContext must be used as a child of CurrentUserContextProvider');
  }

  const now = Date.now();
  const { refetch, expiryTime } = context;

  const hasExpired = now >= expiryTime;

  useEffect(() => {
    if (hasExpired) {
      refetch();
    }
  }, [hasExpired, refetch]);

  return context;
};
